/// <reference types="vite/client" />
// Database Client
// Handles database operations via custom backend API

// @ts-ignore -- import.meta provided by Vite build; module target set by bundler
const API_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL)
    ? import.meta.env.VITE_API_URL
    : 'https://soundope.com';

class DatabaseClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
  }

  // Helper to get CSRF token from cookie
  private getCsrfToken(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const csrfCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf_token='));
    if (!csrfCookie) return undefined;

    const separatorIndex = csrfCookie.indexOf('=');
    if (separatorIndex === -1) return undefined;

    return csrfCookie.substring(separatorIndex + 1);
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const csrfToken = this.getCsrfToken();

    console.log(`[Database Client] Requesting: ${url}`, { method: options.method || 'GET' });

    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Always include cookies for authentication
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method || '')) 
          ? { 'X-CSRF-Token': csrfToken } 
          : {},
        ...options.headers,
      },
    });

    console.log(`[Database Client] Response status: ${response.status} for ${url}`);

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error(`[Database Client] API error for ${url}:`, errorText);
      throw new Error(`API error: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    console.log(`[Database Client] Response data length:`, Array.isArray(data) ? data.length : 'not an array');
    return data;
  }

  // Tracks
  async getTracks(filters?: { is_active?: boolean; order_by?: string; order?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.order_by) params.append('order_by', filters.order_by);
    if (filters?.order) params.append('order', filters.order);
    if (filters?.limit) params.append('limit', String(filters.limit));

    const query = params.toString();
    return this.request(`/api/tracks${query ? `?${query}` : ''}`);
  }

  async getTrack(id: string) {
    return this.request(`/api/tracks/${id}`);
  }

  // Profiles
  async getProfile(id?: string) {
    const params = id ? `?id=${id}` : '';
    const profiles = await this.request(`/api/profiles${params}`);
    return profiles[0] || null;
  }

  async getProfiles(filters?: { id?: string; email?: string; username?: string }) {
    const params = new URLSearchParams();
    if (filters?.id) params.append('id', filters.id);
    if (filters?.email) params.append('email', filters.email);
    if (filters?.username) params.append('username', filters.username);

    const query = params.toString();
    return this.request(`/api/profiles${query ? `?${query}` : ''}`);
  }

  // Feedback
  async getFeedback(trackId?: string) {
    const params = trackId ? `?track_id=${trackId}` : '';
    return this.request(`/api/feedback${params}`);
  }

  // Comments
  async getComments(trackId?: string) {
    const params = trackId ? `?track_id=${trackId}` : '';
    return this.request(`/api/comments${params}`);
  }

  // Get votes
  async getVotes(filters?: { track_id?: string; voter_id?: string }) {
    const params = new URLSearchParams();
    if (filters?.track_id) params.append('track_id', filters.track_id);
    if (filters?.voter_id) params.append('voter_id', filters.voter_id);

    const query = params.toString();
    return this.request(`/api/votes${query ? `?${query}` : ''}`);
  }

  // Get notifications
  async getNotifications(filters?: { user_id?: string; is_read?: boolean }) {
    const params = new URLSearchParams();
    if (filters?.user_id) params.append('user_id', filters.user_id);
    if (filters?.is_read !== undefined) params.append('is_read', String(filters.is_read));

    const query = params.toString();
    return this.request(`/api/notifications${query ? `?${query}` : ''}`);
  }

  // Get user roles
  async getUserRoles(userId?: string) {
    const params = userId ? `?user_id=${userId}` : '';
    return this.request(`/api/user-roles${params}`);
  }

  // Get tracks by artist
  async getTracksByArtist(artistId: string, isActive?: boolean) {
    const params = isActive !== undefined ? `?is_active=${isActive}` : '';
    return this.request(`/api/tracks/artist/${artistId}${params}`);
  }

  // Get feedback by reviewer
  async getFeedbackByReviewer(reviewerId: string) {
    return this.request(`/api/feedback/reviewer/${reviewerId}`);
  }

  // Get public profile
  async getPublicProfile(id: string) {
    const profiles = await this.request(`/api/public-profiles?id=${id}`);
    return profiles || null;
  }

  // For compatibility with Supabase-style queries
  from(table: string) {
    const self = this;
    return {
      select: (columns: string = '*') => {
        let filters: any = {};
        let orderBy: string | null = null;
        let orderDir: 'asc' | 'desc' = 'desc';
        let limitNum: number | null = null;
        let isActive: boolean | null = null;

        // Helper to execute query and return Promise
        const executeQuery = async (): Promise<{ data: any; error: any }> => {
          try {
            let data: any[] = [];

            if (table === 'tracks') {
              if (filters.artist_id) {
                data = await self.getTracksByArtist(filters.artist_id, isActive ?? true);
              } else {
                data = await self.getTracks({
                  is_active: isActive ?? true,
                  order_by: orderBy || 'created_at',
                  order: orderDir,
                  limit: limitNum || undefined
                });

                // Apply date filter if set (for leaderboard monthly filter)
                if (filters.dateFilter && filters.dateFilter.column && filters.dateFilter.value) {
                  data = data.filter((track: any) => {
                    const trackDate = new Date(track[filters.dateFilter.column]);
                    const filterDate = new Date(filters.dateFilter.value);
                    return trackDate >= filterDate;
                  });
                }
              }
            } else if (table === 'feedback' && filters.track_id) {
              data = await self.getFeedback(filters.track_id);
            } else if (table === 'feedback' && filters.reviewer_id) {
              data = await self.getFeedbackByReviewer(filters.reviewer_id);
            } else if (table === 'comments' && filters.track_id) {
              data = await self.getComments(filters.track_id);
            } else if (table === 'comments') {
              data = await self.getComments();
            } else if (table === 'user_roles' && filters.user_id) {
              data = await self.getUserRoles(filters.user_id);
            } else if (table === 'profiles' && filters.id) {
              const profile = await self.getProfile(filters.id);
              data = profile ? [profile] : [];
            } else if (table === 'profiles') {
              data = await self.getProfiles(filters);
            } else if (table === 'notifications') {
              data = await self.getNotifications(filters);
            } else if (table === 'votes') {
              data = await self.getVotes(filters);
            }

            return { data, error: null };
          } catch (error: any) {
            return { data: null, error: { message: error.message } };
          }
        };

        const queryBuilder: any = {
          eq: (column: string, value: any) => {
            if (column === 'is_active') {
              isActive = value === true || value === 'true';
            } else if (column === 'id' && table === 'profiles') {
              filters.id = value;
            } else if (column === 'id' && table === 'tracks') {
              filters.id = value;
            } else if (column === 'track_id') {
              filters.track_id = value;
            } else if (column === 'reviewer_id') {
              filters.reviewer_id = value;
            } else if (column === 'artist_id') {
              filters.artist_id = value;
            } else if (column === 'user_id' && table === 'user_roles') {
              filters.user_id = value;
            } else if (column === 'user_id') {
              filters.user_id = value;
            }
            return queryBuilder;
          },
          gte: (column: string, value: any) => {
            // Store date filter for later processing
            filters.dateFilter = { column, value };
            return queryBuilder;
          },
          in: (column: string, values: any[]) => {
            filters[column] = values;
            return queryBuilder;
          },
          order: (column: string, options?: { ascending?: boolean }) => {
            orderBy = column;
            orderDir = options?.ascending ? 'asc' : 'desc';
            return queryBuilder;
          },
          limit: (num: number) => {
            limitNum = num;
            return queryBuilder;
          },
          single: async () => {
            try {
              if (table === 'profiles' && filters.id) {
                const data = await self.getProfile(filters.id);
                return { data, error: data ? null : { message: 'Not found' } };
              }
              if (table === 'tracks' && filters.id) {
                const data = await self.getTrack(filters.id);
                return { data, error: data ? null : { message: 'Not found' } };
              }
              if (table === 'public_profiles' && filters.id) {
                const data = await self.getPublicProfile(filters.id);
                return { data, error: data ? null : { message: 'Not found' } };
              }
              return { data: null, error: { message: 'Not found' } };
            } catch (error: any) {
              return { data: null, error: { message: error.message } };
            }
          },
          maybeSingle: async () => {
            return queryBuilder.single();
          },
          // Make it awaitable - return Promise that resolves to { data, error }
          then: (onResolve: any, onReject?: any) => {
            return executeQuery().then(onResolve, onReject);
          },
          catch: (onReject: any) => {
            return executeQuery().catch(onReject);
          },
        };

        return queryBuilder;
      },
      insert: (data: any) => {
        const insertData = Array.isArray(data) ? data : [data];

        // Lazy execution; reused for all awaits
        let lazyPromise: Promise<{ data: any; error: any }> | null = null;
        const execute = (): Promise<{ data: any; error: any }> => {
          if (!lazyPromise) {
            lazyPromise = (async () => {
              try {
                const csrfToken = self.getCsrfToken();
                const endpoint = table === 'votes' ? '/api/votes' : `/api/${table}`;
                const response = await fetch(`${self.baseUrl}${endpoint}`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
                  },
                  body: JSON.stringify(insertData[0]),
                });

                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({ error: response.statusText }));
                  return { data: null, error: { message: errorData.error || 'Insert failed' } };
                }

                const result = await response.json();
                return { data: result, error: null };
              } catch (error: any) {
                return { data: null, error: { message: error.message || 'Insert failed' } };
              }
            })();
          }
          return lazyPromise;
        };

        const insertBuilder: any = {
          select: (columns: string = '*') => insertBuilder, // No-op for compatibility
          single: () => execute(),
          then: (onResolve: any, onReject?: any) => execute().then(onResolve, onReject),
          catch: (onReject: any) => execute().catch(onReject),
          finally: (onFinally: any) => execute().finally(onFinally),
        };

        return insertBuilder;
      },
      update: (data: any) => {
        const updateFilters: any = {};
        const updateData = data;

        // Lazy execution function - only creates promise when called
        let lazyPromise: Promise<{ data: any; error: any }> | null = null;
        const execute = (): Promise<{ data: any; error: any }> => {
          if (!lazyPromise) {
            lazyPromise = (async () => {
              try {
                // Build query string from filters
                const filterPairs = Object.entries(updateFilters).map(([key, val], idx) =>
                  `${key}=${encodeURIComponent(String(val))}`
                );
                const queryString = filterPairs.length > 0 ? `?${filterPairs.join('&')}` : '';

                // Call update endpoint
                const csrfToken = self.getCsrfToken();
                const response = await fetch(`${self.baseUrl}/api/${table}${queryString}`, {
                  method: 'PATCH',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
                  },
                  body: JSON.stringify(updateData),
                });

                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({ error: response.statusText }));
                  return { data: null, error: { message: errorData.error || 'Update failed' } };
                }

                const result = await response.json();
                return { data: result, error: null };
              } catch (error: any) {
                return { data: null, error: { message: error.message || 'Update failed' } };
              }
            })();
          }
          return lazyPromise;
        };

        const updateBuilder: any = {
          eq: (column: string, value: any) => {
            updateFilters[column] = value;
            return updateBuilder; // Return self to allow chaining
          },
          then: (onResolve: any, onReject?: any) => {
            return execute().then(onResolve, onReject);
          },
          catch: (onReject: any) => {
            return execute().catch(onReject);
          },
          finally: (onFinally: any) => {
            return execute().finally(onFinally);
          },
        };

        return updateBuilder;
      },
      delete: () => ({
        eq: (column: string, value: any) => ({
          then: async (callback: any) => {
            // TODO: Implement delete endpoints
            return callback({ data: null, error: null });
          },
        }),
      }),
    };
  }

  channel(channelName: string) {
    return {
      on: (event: string, config: any, callback?: (payload: any) => void) => ({
        subscribe: (handler?: (payload: any) => void) => {
          // For now, just return a no-op unsubscribe function
          // Real-time subscriptions would need WebSocket implementation
          // TODO: Implement WebSocket-based real-time subscriptions
          return {
            unsubscribe: () => {}
          };
       },
      }),
    };
  }

  removeChannel(channel: { unsubscribe?: () => void } | null) {
    // Channel cleanup - if channel has unsubscribe, call it
    if (channel && typeof channel.unsubscribe === 'function') {
      channel.unsubscribe();
    }
    // No-op for now since we're not implementing real-time subscriptions yet
  }

  // RPC methods
  async rpc(functionName: string, params?: any): Promise<{ data: any; error: any }> {
    try {
      const csrfToken = this.getCsrfToken();

      if (functionName === 'increment_track_praise_count') {
        // Use the dedicated endpoint
        const response = await fetch(`${this.baseUrl}/api/tracks/${params.p_track_id}/praise`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify({ amount: params.p_amount || 1 }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          return { data: null, error: { message: errorData.error || 'RPC failed' } };
        }

        const result = await response.json();
        return { data: result.praise_count, error: null };
      } else if (functionName === 'award_review_credit') {
        // TODO: Implement award_review_credit endpoint
        return { data: null, error: { message: 'award_review_credit not yet implemented' } };
      } else if (functionName === 'deduct_credits') {
        // Handle credit deduction - map p_amount to amount for backend compatibility
        const amount = params.p_amount || params.amount;
        const authToken = localStorage.getItem('auth_token');
        
        const response = await fetch(`${this.baseUrl}/api/credits/deduct`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({ 
            amount,
            use_premium: true, // Use premium_credits for slot machine
            transaction_type: params.p_transaction_type || 'deduct',
            reference_type: params.p_reference_type || null,
            metadata: params.p_metadata || {}
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          return { data: null, error: { message: errorData.error || 'Credit deduction failed' } };
        }

        const result = await response.json();
        return { data: result, error: null };
      } else if (functionName === 'add_credits') {
        // Handle credit addition - map p_amount to amount for backend compatibility
        const amount = params.p_amount || params.amount;
        const authToken = localStorage.getItem('auth_token');
        
        const response = await fetch(`${this.baseUrl}/api/credits/add`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({ 
            amount,
            use_premium: true, // Use premium_credits for slot machine rewards
            transaction_type: params.p_transaction_type || 'slot_prize',
            reference_type: params.p_reference_type || null,
            metadata: params.p_metadata || {}
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          return { data: null, error: { message: errorData.error || 'Credit addition failed' } };
        }

        const result = await response.json();
        return { data: result, error: null };
      } else if (functionName === 'add_leaderboard_votes') {
        // Use RPC-style endpoint to add leaderboard votes for a user
        const authToken = localStorage.getItem('auth_token');

        const response = await fetch(`${this.baseUrl}/api/rpc/add_leaderboard_votes`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({
            p_user_id: params.user_id,
            p_amount: params.amount,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          return { data: null, error: { message: errorData.error || 'Vote addition failed' } };
        }

        const result = await response.json();
        return { data: result.data ?? result, error: null };
      } else if (functionName === 'move_to_leaderboard_spot') {
        // Use RPC-style endpoint to move a user's track to a specific leaderboard spot
        const authToken = localStorage.getItem('auth_token');

        const response = await fetch(`${this.baseUrl}/api/rpc/move_to_leaderboard_spot`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({
            p_user_id: params.user_id,
            p_target_spot: params.target_spot,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          return { data: null, error: { message: errorData.error || 'Move to spot failed' } };
        }

        const result = await response.json();
        return { data: result.data ?? result, error: null };
      }

      return { data: null, error: { message: `Unknown RPC function: ${functionName}` } };
    } catch (error: any) {
      return { data: null, error: { message: error.message || 'RPC failed' } };
    }
  }
}

// Export the database client instance
export const databaseClient = new DatabaseClient();

// Also export the class for cases where a fresh instance is needed
export { DatabaseClient };
